import Pagination from "components/pagination";
import { useCreate, useTable } from "@refinedev/core";
import { FullScreenLoading } from "components/fullScreenLoading";
import { CreateEventStep, EventType } from "interfaces";
import { EventCard, Filter } from "components/shared/event";
import { _openUrlOnNewTab, isHaveActionPermission } from "utills/helpers";
import { get } from "lodash";
import { useTranslation } from "@refinedev/core";
import { Breadcrumbs } from "components/breadcrumbs";
import Button from "components/button";
import { Link } from "react-router-dom";
import { PlusIcon } from "@heroicons/react/outline";

const DEFAULT_PAGE_SIZE = 9;

export const WPSportEventList: React.FC = () => {
  const { translate } = useTranslation();
  const { tableQuery, filters, setFilters } = useTable<any>({
    resource: "events/sports",
    syncWithLocation: true,
    pagination: {
      mode: "off",
      pageSize: DEFAULT_PAGE_SIZE,
    },
    meta: {
      resource: "data",
    },
  });

  const { mutate: createMutation } = useCreate();

  const _redirectPreviewPage = (eventId: string) => {
    createMutation(
      {
        resource: "events/preview",
        meta: {
          ignoreResourceWrapper: true,
        },
        values: {
          event_id: eventId,
        },
      },
      {
        onSuccess: (data) => {
          const previewUrl = get(data, "data.data.preview_url", "/");
          _openUrlOnNewTab(previewUrl);
        },
      }
    );
  };

  const _handlePageChanged = (page: number) => {
    const updatedFilters = [...filters].flatMap((item) =>
      "field" in item ? item : []
    );
    const pageIndex = updatedFilters.findIndex(
      (filter) => filter.field === "page"
    );
    if (pageIndex < 0) {
      updatedFilters.push({ field: "page", operator: "eq", value: page });
    } else {
      updatedFilters[pageIndex].value = page;
    }

    setFilters(updatedFilters);
  };

  if (tableQuery.isLoading) {
    return <FullScreenLoading />;
  }
  const breadcrumbItems = [
    {
      label: translate("sport-events-management"),
      href: "/events/sports",
    },
  ];

  return (
    <>
      <Breadcrumbs
        type="wps"
        breadcrumbItems={breadcrumbItems}
        disableBorder={true}
        additionalLabel={(
          <>
            {isHaveActionPermission("sports", "events", "create") ? (
              <div className="flex items-center gap-x-2">
                <Link to={"/events/sports/create"} target="_blank">
                  <Button className="w-11 h-11 sm:w-auto">
                    <span className="sr-only">{translate("create-event")}</span>
                    <PlusIcon className="w-4 h-4 text-white" />{" "}
                    <span className="hidden sm:block sm:ml-2">{translate("create-event")}</span>
                  </Button>
                </Link>
              </div>
            ) : <></>}
          </>
        )}
      />
      <div className="wrapper p-4">
        <div className="box-container">
          <Filter filters={filters} setFilters={setFilters} pagingType="page" />
        </div>
        <div className="box-container">
          <EventCard
            eventType={EventType.SPORT}
            data={tableQuery?.data?.data}
            pageSize={tableQuery?.data?.pageSize}
            currentPage={tableQuery.data?.paging.current_page}
            {...(isHaveActionPermission("sports", "events", "edit") && {
              primaryButton: (id: string) => ({
                label: translate("edit"),
                href: `/events/sports/${id}/edit?step=${CreateEventStep.BUILD_EVENT_PAGE}`,
              })
            })}
            moreActions={(id: string) => [
              {
                label: translate("view-detail"),
                href: `/events/sports/${id}?step=${CreateEventStep.BUILD_EVENT_PAGE}`,
                target: "_blank",
              },
              {
                label: translate("preview"),
                onClick: () => _redirectPreviewPage(id),
              },
            ]}
          />
          <div className="mt-6">
            <Pagination
              currentPage={tableQuery?.data?.paging.current_page}
              totalPage={tableQuery?.data?.paging.total_page}
              total={{
                totalItems: tableQuery?.data?.paging.total_item || 0,
                total: tableQuery?.data?.paging.total_event || 0,
              }}
              onChange={_handlePageChanged}
            />
          </div>
        </div>
        {tableQuery.isFetching && tableQuery.isPreviousData ? (
          <FullScreenLoading />
        ) : (
          <></>
        )}
      </div>
    </>
  );
};
